import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import type { User } from "@harmonyso/users-sdk";
import { UserCheck, AlertTriangle } from "lucide-react";
import { LoginIdSelector } from "./login-id-selector";

interface ImpersonateDialogProps {
  user: User;
  tenantId: string;
  onConfirm: (tenantId: string, loginId: string) => void;
  trigger: React.ReactNode;
}

export function ImpersonateDialog({
  user,
  tenantId,
  onConfirm,
  trigger,
}: ImpersonateDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLoginId, setSelectedLoginId] = useState<string | null>(null);

  const handleConfirm = () => {
    if (selectedLoginId) {
      onConfirm(tenantId, selectedLoginId);
      setIsOpen(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-orange-600" />
            Impersonate User
          </DialogTitle>
          <DialogDescription>
            You are about to impersonate another user. This action will be
            logged and recorded for audit purposes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-800 dark:bg-orange-950">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-orange-600 dark:text-orange-400" />
            <div className="text-sm text-orange-800 dark:text-orange-200">
              <p className="mb-1 font-medium">Important Security Notice</p>
              <p>This action will:</p>
              <ul className="mt-2 list-inside list-disc space-y-1">
                <li>Grant you full access to their account</li>
                <li>Log all actions under their identity</li>
                <li>Be recorded for audit purposes</li>
              </ul>
            </div>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-[100px_1fr] gap-2 text-sm">
              <span className="text-muted-foreground font-medium">User:</span>
              <span className="font-mono">{user.display_name}</span>
            </div>
            <div className="grid grid-cols-[100px_1fr] gap-2 text-sm">
              <span className="text-muted-foreground font-medium">Email:</span>
              <span className="font-mono">{user.email}</span>
            </div>
            <div className="grid grid-cols-[100px_1fr] gap-2 text-sm">
              <span className="text-muted-foreground font-medium">
                User ID:
              </span>
              <span className="font-mono text-xs">{user.user_id}</span>
            </div>

            <LoginIdSelector
              user={user}
              tenantId={tenantId}
              selectedLoginId={selectedLoginId}
              onLoginIdChange={setSelectedLoginId}
            />
          </div>
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-row">
          <Button
            variant="outline"
            onClick={() => {
              setIsOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedLoginId}
            className="bg-orange-600 hover:bg-orange-700 disabled:bg-gray-400"
          >
            Confirm Impersonation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
